import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Acessibilidade de modais sem dependência externa:
 * - foca o primeiro elemento focável ao abrir
 * - mantém o foco preso dentro do container (Tab no último volta pro primeiro, Shift+Tab no primeiro volta pro último)
 * - chama `onEscape` quando Esc é pressionado (use pra fechar o modal)
 *
 * <p>Quando `active=false`, listeners são removidos — usar com o mesmo flag
 * que controla a visibilidade do dialog (`isOpen`).</p>
 *
 * <p>Não restaura foco no elemento anterior ao fechar (Outline pequeno
 * — adicionar se aparecer necessidade real; padrão browser já cobre a maior parte).</p>
 */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  active: boolean,
  onEscape?: () => void
) {
  // Foco inicial: SÓ na ativação (deps sem onEscape). Antes isto morava no mesmo
  // effect do listener, que tinha `onEscape` nas deps — se o caller passa um
  // onEscape recriado a cada render (comum: um handler não-memoizado), o effect
  // re-rodava a cada render e `focusFirst()` roubava o foco pro 1º focável (o X do
  // modal) a cada tecla digitada → inputs só aceitavam 1 caractere. Isolado aqui,
  // o foco inicial ocorre uma vez por abertura.
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    // microtask pra garantir que o DOM já renderizou (componente pode estar
    // montando concomitante com active=true).
    queueMicrotask(() => {
      const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = Array.from(focusables).find(el => el.offsetParent !== null);
      first?.focus();
    });
  }, [active, ref]);

  // Listener de teclado (Tab cicla, Esc fecha): re-inscreve quando onEscape muda,
  // MAS não refoca — por isso separado do foco inicial acima.
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        e.preventDefault();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(el => el.offsetParent !== null);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [active, onEscape, ref]);
}
